import { useCallback, useEffect, useRef, useState } from 'react';
import { Container, makeStyles } from '@material-ui/core';
import ProjectsList from '../components/projects/ProjectsList';
import Modal from '../components/shared/Modal';
import CreateProject from '../components/projects/CreateProject';
import Button from '../components/shared/Button';
import plus from '../assets/icons/plus.svg';
import download from '../assets/icons/download.svg';
import axios from 'axios';
import { useGetCurrentUserQuery, useGetFilteredProjectsMutation, useGetOfficesQuery } from 'utils/wemble-api';
import TeamSelect from 'components/shared/TeamSelect';
import { ProjectWithUsersDto } from 'utils/wemble-api.generated';
import OrderSelect from 'components/shared/OrderSelect';
import { compareNumbers, compareString } from 'helpers/compareFunctions';
import moment from 'moment';
import organization from 'utils/organization';
import PageTitle from 'components/PageTitle';
import { useScrollRestore } from 'hooks/useScrollRestore';

const useStyles = makeStyles((theme) => ({
  container: {
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  projects: {
    width: '100%',
    margin: '0 auto 65px',
    fontFamily: 'Roboto',
  },
  projects__toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  toolbar__tools: {
    display: 'flex',
    '& > div': {
      marginLeft: 16,
    },
  },
}));

const orderOptions: { label: string; value: string }[] = [
  { label: 'Name', value: 'name' },
  { label: 'Start date', value: 'start-date' },
  { label: 'End date', value: 'end-date' },
  { label: 'Members', value: 'members' },
  { label: 'Workload', value: 'current-workload' },
];

const sortFunction = (sortMethod: string) => (projectA: ProjectWithUsersDto, projectB: ProjectWithUsersDto) => {
  switch (sortMethod) {
    default:
    case 'name':
      return compareString(projectA.name, projectB.name);
    case 'start-date':
      return compareNumbers(projectB.startDate ? moment(projectB.startDate).unix() : -1, projectA.startDate ? moment(projectA.startDate).unix() : -1);
    case 'end-date':
      return compareNumbers(projectB.endDate ? moment(projectB.endDate).unix() : -1, projectA.endDate ? moment(projectA.endDate).unix() : -1);
    case 'members':
      return compareNumbers(projectB.users?.length, projectA.users?.length);
    case 'current-workload':
      return compareNumbers(projectB.averageWorkload, projectA.averageWorkload);
  }
};

const Projects = () => {
  const classes = useStyles();
  const [visible, setVisible] = useState(false);
  const { data: user } = useGetCurrentUserQuery();
  const { data: offices } = useGetOfficesQuery();
  useScrollRestore('projects', !!user);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<ProjectWithUsersDto[] | undefined>();
  const [getFilteredProject] = useGetFilteredProjectsMutation();
  const [order, setOrder] = useState(orderOptions[0].value);
  const initializedRef = useRef(false);
  const org = organization(user?.company);

  // Default team selection
  useEffect(() => {
    if (user && offices && !initializedRef.current) {
      initializedRef.current = true;
      const groupId = typeof user.group === 'string' ? user.group : user.group?._id;
      const officeId = typeof user.office === 'string' ? user.office : user.office?._id;

      if (org.projectsAllGroups || org.allGroupsDefault) {
        setSelectedTeams(['__all__']);
      } else if (officeId) {
        setSelectedTeams([officeId]);
      } else {
        setSelectedTeams(['__all__']);
      }
    }
  }, [user, offices]);

  const hasTeams = selectedTeams.length > 0;

  const fetchProjects = useCallback(
    async () => {
      if (!hasTeams) return;
      const isAll = selectedTeams[0] === '__all__';
      const res = await getFilteredProject({
        body: {
          filters: isAll ? {} : { teams: selectedTeams } as any,
        },
      });
      if ('data' in res) {
        setFilteredProjects(res.data);
      }
    },
    [selectedTeams, hasTeams],
  );

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const downloadXlsx = () => {
    axios({
      url: '/api/v2/project/export',
      method: 'POST',
      data: { projectIds: filteredProjects?.map(p => p._id) ?? [] },
      responseType: 'blob',
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'report.xlsx');
      document.body.appendChild(link);
      link.click();
    });
  };

  const toggleModal = () => {
    setVisible(!visible);
  };

  return (
    <>
      <Container className={classes.container}>
        <PageTitle title="Projects" />
        <div className={classes.projects}>
          <br />
          <div className={classes.projects__toolbar}>
            <TeamSelect
              value={selectedTeams}
              onChange={setSelectedTeams}
            />
            <div className={classes.toolbar__tools}>
              <OrderSelect
                options={orderOptions}
                onChange={(order) => {
                  setOrder(order.value);
                }}
                defaultValue={orderOptions[0]}
              />

              <Button onClick={downloadXlsx} icon={<img alt={'+'} src={download} />} />
              <Button onClick={toggleModal} text={'Add project'} icon={<img alt={'+'} src={plus} />} />
            </div>
          </div>
          <br />

          <ProjectsList
            currentUser={user}
            projects={filteredProjects && [...filteredProjects].sort(sortFunction(order))}
          />
          <Modal visible={visible} close={toggleModal}>
            <CreateProject user={user} close={toggleModal} />
          </Modal>
        </div>
      </Container>
    </>
  );
};

export default Projects;
